import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  listOrgIntegrations,
  listUserIntegrations,
} from 'redux/slices/integrationsSlice';

/**
 * Owns the connection state for every integration the Dashboard layout knows
 * about, and the `refreshIntegrations` fetch that fills it in.
 *
 * Split out of `Dashboard.js` in Phase 6. `Dashboard` calls this
 * unconditionally, so the state and all three effects keep exactly the
 * lifetime they had inline.
 *
 * The returned `integrations` object is what `GlobalDialogs` hands to the
 * onboarding wizard's CRM / tools steps — those steps read this connection
 * state and call `refreshIntegrations` after a connect.
 *
 * @returns {{gmailReadOnly: boolean, integrations: object}}
 */
const useDashboardIntegrations = () => {
  const dispatch = useDispatch();
  const userDetails = useSelector((state) => state.userDetails.userDetails);
  const integrationsRefreshSignal = useSelector(
    (state) => state.uiStates.integrationsRefreshSignal,
  );

  const [userIntegrationsMap, setUserIntegrationsMap] = useState({});
  const [orgIntegrationsMap, setOrgIntegrationsMap] = useState({});

  const refreshIntegrations = useCallback(() => {
    dispatch(listUserIntegrations(null))
      .unwrap()
      .then((data) => {
        const map = (data || []).reduce((acc, item) => {
          acc[item.provider] = item;
          return acc;
        }, {});
        setUserIntegrationsMap(map);
        setGoogleIntegration(
          !!(map['GOOGLE_CALENDAR'] || map['UNIPILE_GOOGLE_CALENDAR']),
        );
        setSendMeetingBotAutomatically(
          !!(map['GOOGLE_CALENDAR'] ?? map['UNIPILE_GOOGLE_CALENDAR'])
            ?.sendMeetingBotAutomatically,
        );
        setGmailIntegration(!!map['GMAIL_IMAP']);
        setGmailOAuthIntegration(
          !!(map['GMAIL_OAUTH'] || map['UNIPILE_GMAIL']),
        );
        setToolsConnected(
          !!(map['GOOGLE_CALENDAR'] || map['UNIPILE_GOOGLE_CALENDAR']) ||
            !!map['GMAIL_IMAP'] ||
            !!(map['GMAIL_OAUTH'] || map['UNIPILE_GMAIL']),
        );
        setFathomIntegration(!!map['FATHOM']);
      })
      .catch(() => {});
    dispatch(listOrgIntegrations())
      .unwrap()
      .then((data) => {
        const map = (data || []).reduce((acc, item) => {
          acc[item.provider] = item;
          return acc;
        }, {});
        setOrgIntegrationsMap(map);
        setZoomPrivateAppIntegration(!!map['ZOOM_PRIVATE']);
        setAircallIntegration(!!map['AIRCALL']);
      })
      .catch(() => {});
  }, [dispatch]);

  const [toolsConnected, setToolsConnected] = useState(false);

  // hubspot integration

  // zoho Integration

  // pipedrive integration

  // google calendar integration
  const [googleIntegration, setGoogleIntegration] = useState(false);

  // gmail integration
  const [gmailReadOnly, setGmailReadOnly] = useState(false);
  const [gmailIntegration, setGmailIntegration] = useState(false);

  const [gmailOAuthIntegration, setGmailOAuthIntegration] = useState(false);

  const [
    sendMeetingBotAutomatically,
    setSendMeetingBotAutomatically,
  ] = useState(false);

  // slack integration

  // fathom integration

  const [fathomIntegration, setFathomIntegration] = useState(false);
  const [, setFathomUser] = useState('');

  // zoom integration
  const [zoomPrivateAppIntegration, setZoomPrivateAppIntegration] = useState(
    false,
  );

  // aircall integration
  const [aircallIntergration, setAircallIntegration] = useState(false);

  useEffect(() => {
    if (userDetails) {
      setGmailReadOnly(
        userDetails?.custom?.userIntegration?.gmailOAuth
          ? !userDetails?.custom?.userIntegration?.gmailOAuthWriteAccess
          : userDetails?.custom?.userIntegration?.gmail
          ? !userDetails?.custom?.userIntegration?.gmailReadWrite
          : false,
      );
      setFathomUser(userDetails?.custom?.fathomUser?.crmEmail);
    }
  }, [userDetails]);

  useEffect(() => {
    if (userDetails?.id) {
      refreshIntegrations();
    }
  }, [userDetails?.id]);

  // The shared integration dialogs in `src/features/integrations/` are mounted
  // once, from `GlobalDialogs`, so they cannot call this file's
  // `refreshIntegrations` directly. They bump `integrationsRefreshSignal`
  // instead and every mounted page re-runs its own refresh.
  useEffect(() => {
    if (integrationsRefreshSignal) {
      refreshIntegrations();
    }
  }, [integrationsRefreshSignal]);

  return {
    gmailReadOnly,
    integrations: {
      refreshIntegrations,
      userIntegrationsMap,
      orgIntegrationsMap,
      setOrgIntegrationsMap,
      googleIntegration,
      gmailIntegration,
      gmailOAuthIntegration,
      fathomIntegration,
      zoomPrivateAppIntegration,
      setZoomPrivateAppIntegration,
      aircallIntergration,
      setAircallIntegration,
      sendMeetingBotAutomatically,
      setSendMeetingBotAutomatically,
      toolsConnected,
    },
  };
};

export default useDashboardIntegrations;
